<template>
  <div class="table-style-panel">

    <div class="style-section">
      <div class="style-row"><span class="label-long">Rows</span> <InputNumber style="width: 100px" v-model="rowCount" showButtons class="row-col-input" :min="0" :max="50" @click="setTableRow(rowCount)"></InputNumber></div>
      <div class="style-row"><span class="label-long">Columns</span> <InputNumber style="width: 100px" v-model="colCount" showButtons class="row-col-input" :min="0" :max="50" @click="setTableCol(colCount)"></InputNumber></div>
      <div class="style-row" v-if="selectedTableCell.col >= 0">
        <span class="label-long">Column Width</span>
        <InputNumber v-model="columnWidth" showButtons style="width: 100px" suffix="%" :min="0"></InputNumber>
      </div>
      <div class="style-row" v-if="selectedTableCell.row >= 0">
        <span class="label-long">Row Height</span>
        <InputNumber v-model="rowHeight" showButtons style="width: 100px" suffix="%" :min="0"></InputNumber>
      </div>

      <div v-if="containsHookId">
        <div style="margin-top: 20px">Text Position</div>
        <SelectButton v-model="textPosition" :options="textPositionOptions"/>


        <div style="margin-top: 20px">Model Field</div>
        <SelectButton v-model="modelField" :options="modelOptions"/>

        <div v-if="modelField === 'Value'">
          <div style="margin-top: 20px">Editor</div>
          <SelectButton v-model="modelEditor" :options="modelEditorOptions"/>
        </div>

      </div>
    </div>

    <Accordion>
      <AccordionTab :disabled="!cellActive" header="Table Filters">
        <TableFiltersEditor v-if="cellActive" :table-element="selectedWidget" :selected-cell-row="selectedCellRow" :selected-cell-column="selectedCellColumn" />
      </AccordionTab>
      <AccordionTab header="Border Style">
        <ElementOutline />
      </AccordionTab>
      <AccordionTab :disabled="!theme" header="Theme">
        <div class="group">
          <div style="flex:1;">
            <Checkbox v-model="theme.rowHeader" binary inputId="rowHeader" @change="updateTheme({ rowHeader: theme.rowHeader })" />
            <label for="rowHeader" style="padding-left:12px;">Header Row</label>
          </div>

          <div style="flex:1;">
            <Checkbox v-model="theme.rowFooter" binary inputId="rowFooter" @change="updateTheme({ rowFooter: theme.rowFooter })" />
            <label for="rowFooter" style="padding-left:12px;"> Footer Row </label>
          </div>
        </div>
        <div class="group">
          <div style="flex:1;">
            <Checkbox v-model="theme.colHeader" binary inputId="colHeader" @change="updateTheme({ colHeader: theme.colHeader })" />
            <label for="colHeader" style="padding-left:12px;">First Column</label>
          </div>

          <div style="flex:1;">
            <Checkbox v-model="theme.colFooter" binary inputId="colFooter" @change="updateTheme({ colFooter: theme.colFooter })" />
            <label for="rowFooter" style="padding-left:12px;">Last Column</label>
          </div>
        </div>

        <div class="group">
          <div style="flex: 2;">Theme Color：</div>
          <ColorButton @click="$refs.themeColorPickerVisible.toggle($event)" :color="theme.color || '#000'" style="flex: 4" />
          <OverlayPanel ref="themeColorPickerVisible">
            <ColorPicker
                :modelValue="theme.color"
                @update:modelValue="value => updateTheme({ color: value })"
            />
          </OverlayPanel>
        </div>
      </AccordionTab>
    </Accordion>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref, watch} from 'vue'
import { storeToRefs } from 'pinia'
import { nanoid } from 'nanoid'
import {useMainStore, useSlidesStore, useWidgetStore} from '@/store'
import { PPTTableElement, TableCell, TableCellStyle, TableTheme } from '@/types/slides'
import { WEB_FONTS } from '@/configs/font'
import useHistorySnapshot from '@/hooks/useHistorySnapshot'

import ElementOutline from '../common/ElementOutline.vue'
import ColorButton from '../common/ColorButton.vue'
import TextColorButton from '../common/TextColorButton.vue'
import ColorPicker from '@/components/ColorPicker/index.vue'

import useMicroservice from "@/hooks/useMicroservice";
import ValueModelAutoComplete from "@/views/Editor/CanvasTool/components/ValueModelAutoComplete.vue";
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue";
import TableFiltersEditor from "./TableFiltersEditor.vue";
import InputNumber from "primevue/inputnumber";
import Button from "primevue/button";
const slidesStore = useSlidesStore()

const { getActiveHookId, getComputedReadOnly, getComputedSource, getComputedNotes, getComputedLabel } = useComputedTreeValue()

const { handleElement, handleElementId, selectedTableCells: selectedCells } = storeToRefs(useMainStore())
const { selectedWidget, selectedTableCell }  = storeToRefs(useWidgetStore())

const fontSizeOptions = [
  '12px', '14px', '16px', '18px', '20px', '22px', '24px', '28px', '32px',
]

const textPositionOptions = ref([
  'Top',
  'Bottom',
  'Left',
  'Right',
  'None'
])

const modelOptions = ref([
  'Name',
  'Value',
  'Notes',
  'Source',
  'None'
])

const modelEditorOptions = ref([
  'Text',
  'Checkbox',
  'None'
])

const modelField = computed({
      get() {
        const mapping =  selectedCell.value?.mapping?? 'value'
        switch (mapping) {
          case 'name':
            return "Name"
          case 'value':
            return "Value"
          case 'notes':
            return "Notes"
          case 'source':
            return "Source"
          case 'none':
            return "None"
        }
        return "Value"
      },
  set(val) {
    if (!selectedWidget.value) {
      return
    }

    for (let i = 0; i < selectedCells.value.length; i++) {
      const rowIndex = selectedCells.value[i].split('_')[0]
      const colIndex = selectedCells.value[i].split('_')[1]
      if (selectedWidget.value.data && selectedWidget.value.data[rowIndex][colIndex]) {
        const selectedCell = selectedWidget.value.data[rowIndex][colIndex]

        switch (val) {
          case 'Name':
            selectedCell.mapping = 'name'
            break
          case 'Value':
            selectedCell.mapping = 'value'
            break
          case 'Notes':
            selectedCell.mapping = 'notes'
            break
          case 'Source':
            selectedCell.mapping = 'source'
            break
          case 'None':
            selectedCell.mapping = 'none'
            break
        }
      }
    }
  }
})

const modelEditor = computed({
  get() {
    const mapping =  selectedCell.value?.mappingEditor?? 'text'
    switch (mapping) {
      case 'text':
        return "Text"
      case 'checkbox':
        return "Checkbox"
      case 'none':
        return "None"
    }
    return "Text"
  },
  set(val) {
    if (!selectedWidget.value) {
      return
    }

    for (let i = 0; i < selectedCells.value.length; i++) {
      const rowIndex = selectedCells.value[i].split('_')[0]
      const colIndex = selectedCells.value[i].split('_')[1]
      if (selectedWidget.value.data && selectedWidget.value.data[rowIndex][colIndex]) {
        const selectedCell = selectedWidget.value.data[rowIndex][colIndex]

        switch (val) {
          case 'Text':
            selectedCell.mappingEditor = 'text'
            break
          case 'Checkbox':
            selectedCell.mappingEditor = 'checkbox'
            break
          case 'None':
            selectedCell.mappingEditor = 'none'
            break
        }
      }
    }
  }
})



const selectedCell = computed(() => {
  if (selectedCells.value.length) {
    const selectedCell = selectedCells.value[0]
    const rowIndex = +selectedCell.split('_')[0]
    const colIndex = +selectedCell.split('_')[1]
    if (selectedWidget.value?.data) {
      return selectedWidget.value?.data[rowIndex][colIndex]
    }
  }
  return null
})

const containsHookId = computed(() => {
  for (let i = 0; i < selectedCells.value.length; i++) {
    const rowIndex = selectedCells.value[i].split('_')[0]
    const colIndex = selectedCells.value[i].split('_')[1]
    if (selectedWidget.value.data && selectedWidget.value?.data.length > rowIndex &&
        selectedWidget.value?.data[rowIndex].length > colIndex
        && selectedWidget.value.data[rowIndex][colIndex]) {
      const selectedCell = selectedWidget.value.data[rowIndex][colIndex]
      if (selectedCell.hookId) {
        return true
      }
    }
  }
})

const textPosition = computed({
  get() {
    const position =  selectedCell.value && selectedCell.value?.labelPosition || 'none'
    switch (position) {
      case 'top':
        return "Top"
      case 'bottom':
        return "Bottom"
      case 'left':
        return "Left"
      case 'right':
        return "Right"
      case 'none':
        return "None"
    }
    return "Top"

  },
  set(val) {
    for (let i = 0; i < selectedCells.value.length; i++) {
      const rowIndex = selectedCells.value[i].split('_')[0]
      const colIndex = selectedCells.value[i].split('_')[1]
      if (selectedWidget.value.data && selectedWidget.value.data[rowIndex][colIndex]) {
        const selectedCell = selectedWidget.value.data[rowIndex][colIndex]

        switch (val) {
          case 'Top':
            selectedCell.labelPosition = 'top'
            break
          case 'Bottom':
            selectedCell.labelPosition = 'bottom'
            break
          case 'Left':
            selectedCell.labelPosition = 'left'
            break
          case 'Right':
            selectedCell.labelPosition = 'right'
            break
          case 'None':
            selectedCell.labelPosition = 'none'
            break
        }
      }
    }
  }
})

const textAttrs = ref({
  bold: false,
  em: false,
  underline: false,
  strikethrough: false,
  color: '#000',
  backcolor: '',
  fontsize: '12px',
  fontname: 'Arial',
  align: 'left',
})

const theme = ref<TableTheme>()
const hasTheme = ref(false)
const rowCount = ref(0)
const colCount = ref(0)
const minRowCount = ref(0)
const minColCount = ref(0)

const displayProperty = ref()
const compactNum = ref()

const selectedCellRow = ref()
const selectedCellColumn = ref()
const cellActive = ref(false)

const api = useMicroservice()

watch(selectedWidget, async () => {
  if (!selectedWidget.value || selectedWidget.value.type !== 'table') return

  theme.value = selectedWidget.value.theme
  hasTheme.value = !!theme.value

  rowCount.value = selectedWidget.value.data.length
  colCount.value = selectedWidget.value.data[0].length

  minRowCount.value = selectedWidget.value.data.length
  minColCount.value = selectedWidget.value.data[0].length

  await api.savePresentation()
}, { deep: true, immediate: true })

const columnWidth = computed({
  get: () => {
    return Math.round(selectedWidget.value.colWidths[selectedTableCell.value.col] * 100)
  },
  set: val => {
    selectedWidget.value.colWidths[selectedTableCell.value.col] = val / 100
  }
})

const rowHeight = computed({
  get: () => {
    return Math.round(
        selectedWidget.value.rowHeights && selectedWidget.value.rowHeights[selectedTableCell.value.row] ?
        selectedWidget.value.rowHeights[selectedTableCell.value.row] * 100 : 0)
  },
  set: val => {
    selectedWidget.value.rowHeights[selectedTableCell.value.row] = val / 100
  }
})

const { addHistorySnapshot } = useHistorySnapshot()

// Update the text style state of the currently selected cell
const updateTextAttrState = () => {
  if (!selectedWidget.value || selectedWidget.value.type !== 'table') return

  let rowIndex = 0
  let colIndex = 0
  if (selectedCells.value.length) {
    const selectedCell = selectedCells.value[0]
    rowIndex = +selectedCell.split('_')[0]
    colIndex = +selectedCell.split('_')[1]
  }

  displayProperty.value = selectedWidget.value.data[rowIndex][colIndex].displayProperty
  compactNum.value = selectedWidget.value.data[rowIndex][colIndex].compact
  const style = selectedWidget.value.data[rowIndex][colIndex].style

  if (!style) {
    textAttrs.value = {
      bold: false,
      em: false,
      underline: false,
      strikethrough: false,
      color: '#000',
      backcolor: '',
      fontsize: '12px',
      fontname: 'Arial',
      align: 'left',
    }
  }
  else {
    textAttrs.value = {
      bold: !!style.bold,
      em: !!style.em,
      underline: !!style.underline,
      strikethrough: !!style.strikethrough,
      color: style.color || '#000',
      backcolor: style.backcolor || '',
      fontsize: style.fontsize || '12px',
      fontname: style.fontname || 'Arial',
      align: style.align || 'left',
    }
  }
}

watch(selectedCells, () => {
  updateTextAttrState()
  cellActive.value = (selectedCells.value.length === 1)
  getCellData()
})

// Set cell content text style
const updateTextAttrs = (textAttrProp: Partial<TableCellStyle>) => {
  const _handleElement = selectedWidget.value

  const data: TableCell[][] = JSON.parse(JSON.stringify(_handleElement.data))

  for (let i = 0; i < data.length; i++) {
    for (let j = 0; j < data[i].length; j++) {
      if (!selectedCells.value.length || selectedCells.value.includes(`${i}_${j}`)) {
        const style = data[i][j].style || {}
        data[i][j].style = { ...style, ...textAttrProp }
      }
    }
  }
  selectedWidget.value.data = data
  updateTextAttrState()
}

const handleSelectionChange = (selectedOptions: any) => {
  const response = { [selectedOptions[0].value]: !textAttrs.value[selectedOptions[0].value] }
  updateTextAttrs(response)
}

// Update table theme: theme color, header row, summary row, first column, last column
const updateTheme = (themeProp: Partial<TableTheme>) => {
  if (!theme.value) return
  const _theme = { ...theme.value, ...themeProp }
  selectedWidget.value.theme = _theme
}

// Set the number of table rows
const setTableRow = (value: number) => {
  const _handleElement = selectedWidget.value
  const count = _handleElement.data.length

  if (value > count) {
    const rowCells = new Array(colCount.value).fill(null).map(() => ({ id: nanoid(10), colspan: 1, rowspan: 1, text: '' }));
    const newTableCells: TableCell[][] = new Array(value - count).fill(rowCells)

    const tableCells: TableCell[][] = JSON.parse(JSON.stringify(_handleElement.data))
    tableCells.push(...newTableCells)

    selectedWidget.value.height = tableCells.length * 36
    selectedWidget.value.data = tableCells
  }
  else {
    const tableCells: TableCell[][] = _handleElement.data.slice(0, value)
    selectedWidget.value.data = tableCells
  }
}

// Set the number of table columns
const setTableCol = (value: number) => {
  const _handleElement = selectedWidget.value
  const colCount = _handleElement.data[0].length

  let tableCells = _handleElement.data
  let colSizeList = _handleElement.colWidths.map(item => item * _handleElement.width)

  if (value > colCount) {
    tableCells = tableCells.map(item => {
      const cells: TableCell[] = new Array(value - colCount).fill({ id: nanoid(10), colspan: 1, rowspan: 1, text: '' })
      item.push(...cells)
      return item
    })

    const newColSizeList: number[] = new Array(value - colCount).fill(100)
    colSizeList.push(...newColSizeList)
  }
  else {
    tableCells = tableCells.map(item => item.slice(0, value))
    colSizeList = colSizeList.slice(0, value)
  }

  const width = colSizeList.reduce((a, b) => a + b)
  const colWidths = colSizeList.map(item => item / width)

  selectedWidget.value.data = tableCells
  selectedWidget.value.width = width
  selectedWidget.value.colWidths = colWidths
}

const setHookId = (valueModelData: any) => {
  if (!selectedWidget.value || selectedCells.value.length > 1) return

  const cellData = getCellData()

  cellData.hookId = valueModelData.value.hookId
  cellData.text = valueModelData.value.label
  cellData.displayProperty = 'name'

  displayProperty.value = 'name'

  updateTextAttrs({})
}

const getCellData = () => {
  let rowIndex = 0
  let colIndex = 0
  if (selectedCells.value.length) {
    const selectedCell = selectedCells.value[0]
    rowIndex = +selectedCell.split('_')[0]
    colIndex = +selectedCell.split('_')[1]
  }
  selectedCellRow.value = rowIndex
  selectedCellColumn.value = colIndex

  return selectedWidget.value.data[rowIndex][colIndex]
}

</script>

<style scoped>
:deep( .p-button.p-component ) {
  justify-content: center;
}

:deep(.p-inputnumber-input) {
  width: 50px !important;
}
:deep(.p-accordion-header-action) {
  cursor: pointer;
  display: flex;
  align-items: center;
  user-select: none;
  position: relative;
  text-decoration: none;
}

:deep(.p-accordion-header-action:focus) {
  z-index: 1;
}

:deep(.p-accordion-header-text) {
  line-height: 1;
}

:deep(.p-accordion .p-accordion-header .p-accordion-header-link) {
  padding: 1rem;
  border: 1px solid #dee2e6;
  color: #495057;
  background: #f8f9fa;
  font-weight: 600;
  border-radius: 3px;
  transition: box-shadow 0.2s;
  outline-color: transparent;
}
:deep(.p-accordion .p-accordion-header .p-accordion-header-link .p-accordion-toggle-icon) {
  margin-right: 0.5rem;
}
:deep(.p-accordion .p-accordion-header:not(.p-disabled) .p-accordion-header-link:focus-visible) {
  outline: 0 none;
  outline-offset: 0;
  box-shadow: inset 0 0 0 0.2rem #a6d5fa;
}
:deep(.p-accordion .p-accordion-header:not(.p-highlight):not(.p-disabled):hover .p-accordion-header-link) {
  background: #e9ecef;
  border-color: #dee2e6;
  color: #495057;
}
:deep(.p-accordion .p-accordion-header:not(.p-disabled).p-highlight .p-accordion-header-link) {
  background: #f8f9fa;
  border-color: #dee2e6;
  color: #495057;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}
:deep(.p-accordion .p-accordion-header:not(.p-disabled).p-highlight:hover .p-accordion-header-link) {
  border-color: #dee2e6;
  background: #e9ecef;
  color: #495057;
}
:deep(.p-accordion .p-accordion-content) {
  padding: 1rem;
  border: 1px solid #dee2e6;
  background: #ffffff;
  color: #495057;
  border-top: 0;
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  border-bottom-right-radius: 3px;
  border-bottom-left-radius: 3px;
}
:deep(.p-accordion .p-accordion-tab) {
  margin-bottom: 0;
}
:deep(.p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link) {
  border-radius: 0;
}
:deep(.p-accordion .p-accordion-tab .p-accordion-content) {
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}
:deep(.p-accordion .p-accordion-tab:not(:first-child) .p-accordion-header .p-accordion-header-link) {
  border-top: 0 none;
}
:deep(.p-accordion .p-accordion-tab:not(:first-child) .p-accordion-header:not(.p-highlight):not(.p-disabled):hover .p-accordion-header-link, .p-accordion .p-accordion-tab:not(:first-child) .p-accordion-header:not(.p-disabled).p-highlight:hover .p-accordion-header-link) {
  border-top: 0 none;
}
:deep(.p-accordion .p-accordion-tab:first-child .p-accordion-header .p-accordion-header-link) {
  border-top-right-radius: 3px;
  border-top-left-radius: 3px;
}
:deep(.p-accordion .p-accordion-tab:last-child .p-accordion-header:not(.p-highlight) .p-accordion-header-link) {
  border-bottom-right-radius: 3px;
  border-bottom-left-radius: 3px;
}
:deep(.p-accordion .p-accordion-tab:last-child .p-accordion-content) {
  border-bottom-right-radius: 3px;
  border-bottom-left-radius: 3px;
}

:deep(.group) {
  margin-top: 25px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  column-gap: 10px;
}

span.label { display: inline-block; width: 60px }
span.label-long { display: inline-block; width: 100px }
.style-section { margin-top: 20px; margin-bottom: 20px;}
.style-row { margin-top: 5px; width: 300px; align-items: center; display: flex  }
.row-col-input :deep(.p-inputnumber) { width: 170px; }
</style>
